import React, { useCallback, useEffect, useState } from 'react';
import { CloudDownload, Loader, ShieldCheck, WifiOff } from 'lucide-react';
import OfflineEnrollmentSheet from './OfflineEnrollmentSheet';
import BiometricSetup from './BiometricSetup';
import Sheet from '../common/Sheet';
import { OfflineSession } from '../../services/offline/offlineSession';
import { useEntitlement } from '../../hooks/useEntitlement';
import { useCanPerform } from '../../hooks/useCanPerform';
import { useDeviceClass, useOfflineStatus, useOnlineStatus } from '../../hooks/useOfflineDevice';
import { OfflineDisabledError, Packets, PACKETS_CHANGED_EVENT } from '../../services/offline/packetService';
import { isApiError } from '../../dtos/common/api.dto';

/** Packets older than this are refreshed when My Day opens online. */
const STALE_MS = 15 * 60 * 1000;
const REFRESH_EVERY_MS = 60 * 60 * 1000;

const timeLabel = (iso: string) => new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

/**
 * My Day card for Offline Field Mode (Phase 16A, design §6.5). On a phone or tablet:
 * - not set up: offer to set it up (install first when needed);
 * - set up: show how many patients are ready offline and refresh them while online.
 * Hidden on laptops/desktops, for non-clinicians and on plans below Core.
 */
const OfflineReadyCard: React.FC = () => {
  // Offline mode is a Core-and-above plan feature (no per-agency switch)
  const planIncludesOffline = useEntitlement('offlineMode');
  const canStartVisits = useCanPerform('create:visit');
  const deviceClass = useDeviceClass();
  const online = useOnlineStatus();
  const status = useOfflineStatus();
  const [sheetOpen, setSheetOpen] = useState(false);
  const [count, setCount] = useState<number | null>(null);
  const [syncedAt, setSyncedAt] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 16D D2: offer fingerprint / face unlock later if it was skipped at enrollment
  const [hasBiometric, setHasBiometric] = useState(true);
  const [biometricOpen, setBiometricOpen] = useState(false);

  const unlocked = status === 'unlocked';

  const readLocal = useCallback(async () => {
    if (!unlocked) return;
    try {
      setCount((await Packets.list()).length);
      setSyncedAt(await Packets.lastSyncedAt());
    } catch {
      setCount(null);
    }
  }, [unlocked]);

  const refresh = useCallback(async () => {
    if (!unlocked || !online) return;
    setBusy(true);
    setError(null);
    try {
      const result = await Packets.refresh();
      setCount(result.patients);
      setSyncedAt(result.syncedAt);
    } catch (e) {
      // 16D kill switch: packets refused platform-wide; queued work keeps syncing
      if (e instanceof OfflineDisabledError) setError(e.message);
      else setError(isApiError(e) && e.statusCode === 403 ? 'This device is no longer set up for offline use.' : 'Could not update offline patients. Try again later.');
    } finally {
      setBusy(false);
    }
  }, [unlocked, online]);

  useEffect(() => {
    const id = OfflineSession.currentIdentity?.id;
    if (!unlocked || !id) return;
    OfflineSession.biometricCredential(id).then((c) => setHasBiometric(Boolean(c))).catch(() => setHasBiometric(true));
  }, [unlocked, biometricOpen]);

  useEffect(() => {
    readLocal();
    window.addEventListener(PACKETS_CHANGED_EVENT, readLocal);
    return () => window.removeEventListener(PACKETS_CHANGED_EVENT, readLocal);
  }, [readLocal]);

  // Refresh when My Day opens online with stale packets, then hourly while it stays open
  useEffect(() => {
    if (!unlocked || !online) return undefined;
    Packets.lastSyncedAt().then((last) => {
      if (!last || Date.now() - Date.parse(last) > STALE_MS) refresh();
    }).catch(() => undefined);
    const timer = setInterval(refresh, REFRESH_EVERY_MS);
    return () => clearInterval(timer);
  }, [unlocked, online, refresh]);

  if (!planIncludesOffline || !canStartVisits || deviceClass !== 'MOBILE') return null;

  if (!unlocked) {
    return (
      <>
        <section aria-label="Offline mode" className="mb-6 rounded-xl border border-slate-200 bg-white p-4">
          <div className="flex items-start gap-3">
            <WifiOff size={20} className="mt-0.5 shrink-0 text-slate-600" aria-hidden="true" />
            <div className="min-w-0 flex-1">
              <h2 className="text-sm font-semibold text-gray-900">Work without signal</h2>
              <p className="mt-0.5 text-sm text-gray-600">Keep today's patients on this device, encrypted, for homes with poor coverage.</p>
            </div>
          </div>
          <button type="button" onClick={() => setSheetOpen(true)} className="mt-3 min-h-[48px] w-full rounded-lg border border-slate-800 text-sm font-semibold text-slate-800">
            Set up offline mode
          </button>
        </section>
        <OfflineEnrollmentSheet open={sheetOpen} onClose={() => setSheetOpen(false)} onEnrolled={readLocal} />
      </>
    );
  }

  return (
    <section aria-label="Offline mode" className="mb-6 flex flex-wrap items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-3">
      <ShieldCheck size={20} className="shrink-0 text-emerald-700" aria-hidden="true" />
      <div className="min-w-0 flex-1 text-sm">
        <div className="font-semibold text-emerald-900">
          Offline ready{count !== null ? ` · ${count} ${count === 1 ? 'patient' : 'patients'}` : ''}
        </div>
        <div className="text-emerald-800">
          {error || (syncedAt ? `Updated ${timeLabel(syncedAt)}` : 'Not downloaded yet')}
        </div>
      </div>
      <button
        type="button"
        onClick={refresh}
        disabled={busy || !online}
        aria-label="Update offline patients"
        className="flex min-h-[44px] min-w-[44px] items-center justify-center rounded-lg border border-emerald-300 bg-white text-emerald-800 disabled:opacity-50"
      >
        {busy ? <Loader size={18} className="animate-spin" /> : <CloudDownload size={18} />}
      </button>
      {!hasBiometric && (
        <button type="button" onClick={() => setBiometricOpen(true)} className="min-h-[44px] basis-full rounded-lg border border-emerald-300 bg-white px-3 text-sm font-medium text-emerald-900">
          Fingerprint or face unlock
        </button>
      )}
      <Sheet open={biometricOpen} onClose={() => setBiometricOpen(false)} title="Fingerprint or face unlock">
        <BiometricSetup />
      </Sheet>
    </section>
  );
};

export default OfflineReadyCard;
